import React, { useState, useEffect, useRef } from 'react';
import { ensureKakao, shareMalsoLink } from '../utils/kakaoShare';

// 말소증 원본 뷰어 + [업체에 공유] — 완료 목록 / 정산 장부 / 일계표 3곳 공용 (W1, Jin 확정 2026-09-15).
// props: { title, carNo, fetchDoc:()=>Promise<Response>, share:()=>Promise<{url}>, showToast, onClose }
//
// 인증 필요 문서라 <a href>/새 탭은 401 — fetchDoc() 의 Response 를 blob → objectURL 로 연다
// (CancelAgencyDoneList 관례). 부모가 조건부 렌더를 꺼서(onClose 호출 후 상태 제거) 이 컴포넌트가
// 언마운트되면 그 순간 objectURL 을 회수한다 — 닫기와 부모 전체 언마운트가 같은 경로를 탄다.
//
// ⚠️ 카카오 팝업 차단 규칙(utils/kakaoShare.js 와 동일 원칙) — Kakao.Share.sendDefault 는 클릭
//    핸들러 안에서 **동기** 호출돼야 한다. 그런데 공유 링크는 서버 POST(share())로 매번 새로 받아야
//    해서 최초 클릭은 필연적으로 비동기다. 그래서 최초 클릭은 링크 발급 + 클립보드 복사만 하고,
//    링크가 이미 캐시된 다음 클릭부터 카카오를 동기로 부른다(2단계 UI).
function MalsoCertViewer(props) {
  var title = props.title || '말소증';
  var carNo = props.carNo || '';
  var onClose = props.onClose || function () {};
  var showToast = props.showToast || function () {};

  var [doc, setDoc] = useState(null);        // { url, pdf }
  var [loadErr, setLoadErr] = useState('');
  var [shareUrl, setShareUrl] = useState(''); // 서버에서 받은 뒤 캐시 — 재클릭 시 동기 카카오 호출용
  var [shareBusy, setShareBusy] = useState(false);
  var docUrlRef = useRef('');

  useEffect(function () {
    var alive = true;
    var ctype = '';
    props.fetchDoc().then(function (res) {
      if (!res.ok) throw new Error(res.status === 404 ? 'none' : 'fail');
      ctype = (res.headers && res.headers.get && res.headers.get('Content-Type')) || '';
      return res.blob();
    }).then(function (b) {
      if (!alive) return;
      var u = URL.createObjectURL(b);
      docUrlRef.current = u;
      setDoc({ url: u, pdf: ctype.indexOf('application/pdf') >= 0 });
    }).catch(function (e) {
      if (!alive) return;
      setLoadErr(e && e.message === 'none' ? '말소증이 없습니다' : '말소증을 불러오지 못했습니다');
    });
    return function () {
      alive = false;
      if (docUrlRef.current) { try { URL.revokeObjectURL(docUrlRef.current); } catch (e) { /* 이미 해제됨 */ } docUrlRef.current = ''; }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var copyAndToast = function (url) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).catch(function () { window.prompt('공유 링크를 복사하세요', url); });
    } else {
      window.prompt('공유 링크를 복사하세요', url);
    }
    showToast('링크 복사됨 (90일 유효)');
  };

  var doShare = function () {
    if (shareBusy) return;
    // 링크가 이미 있다 — 이 클릭은 새 사용자 제스처이므로 카카오를 동기로 부를 수 있다.
    if (shareUrl) {
      if (ensureKakao()) {
        var ok = shareMalsoLink({ url: shareUrl, carNo: carNo, onError: function (m) { showToast('카카오 공유 실패: ' + m, 'err'); } });
        if (ok) return;
      }
      copyAndToast(shareUrl);
      return;
    }
    // 최초 클릭 — 서버 발급(비동기)이라 카카오를 안전하게 못 부른다. 클립보드로 안내.
    setShareBusy(true);
    props.share().then(function (d) {
      setShareBusy(false);
      var url = d && d.url;
      if (!url) { showToast((d && d.error) || '공유 링크를 만들지 못했습니다', 'err'); return; }
      setShareUrl(url);
      copyAndToast(url);
    }).catch(function () {
      setShareBusy(false);
      showToast('공유 링크를 만들지 못했습니다', 'err');
    });
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: 'min(900px,94vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={function (e) { e.stopPropagation(); }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>✕</button>
        </div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
          {!!loadErr && <div style={{ color: '#94a3b8', padding: 24 }}>{loadErr}</div>}
          {!loadErr && !doc && <div style={{ color: '#94a3b8', padding: 24 }}>불러오는 중…</div>}
          {!loadErr && !!doc && (doc.pdf
            ? <iframe title={title} src={doc.url} style={{ width: '100%', height: '72vh', border: 'none' }} />
            : <img src={doc.url} alt={title} style={{ maxWidth: '100%', maxHeight: '72vh', display: 'block' }} />)}
        </div>
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <button className="btn btn-outline btn-sm" onClick={doShare} disabled={shareBusy}>{shareBusy ? '전송 중…' : '업체에 공유'}</button>
        </div>
      </div>
    </div>
  );
}

export default MalsoCertViewer;
